import React, { useEffect, useRef, useState } from "react";
import { Tabs, Tag, Button, message, Image as AntImage, Rate } from "antd";
import { useContext } from "react";
import { MdOutlineFileDownload, MdCancel } from "react-icons/md";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { socket } from "../../utils/socket"; 
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { ApiContext } from "src/context/ApiContext.jsx";
import {
  BookKey,
  CalendarCheck,
  LocateFixedIcon,
  MessageCircleMore,
  ShoppingCart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Chat from "./Chat";
import { Card } from "../ui/card"; 
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import { format } from "date-fns";
import { Typography } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import Invoice from "../Invoice";
import { GoogleMapProvider } from "../../context/googleMapContext";
import LiveTracking from "../LiveTracking";

const { Title, Text } = Typography;
const token = localStorage.getItem("token");

const BookingHistory = () => {
  const receiptRef = useRef(null);
  const [status, setStatus] = useState("On the Way");  
  const [travelBookings, setTravelBookings] = useState([]);
  const [cancelReason, setCancelReason] = useState("");
  const [ratings, setRatings] = useState({}); // Store ratings per booking
  const [comments, setComments] = useState({}); // Store comments per booking
  const [location, setLocation] = useState({ lat: 12.9716, lng: 77.5946 });
  const [iframeUrl, setIframeUrl] = useState(""); 
  
  const navigate = useNavigate();
  const { Booking, lastBookingData } = useContext(ApiContext);
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userId;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);
const [loading,setLoading] = useState(false);

  const getBooking = async () => {
    setLoading(true);
    try {
      const travelResponse = await axios.get(
        "https://wavetechservices.in/api/user/travelbookings",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ); 
      setLoading(false);
      setTravelBookings(travelResponse.data.bookings);
    } catch (err) {
      console.error("Error:", err);
      setLoading(false);
      message.error("Failed to fetch bookings");
    }
  };

  useEffect(() => {
    getBooking();
  }, []);

  // Socket setup for live tracking
  useEffect(() => {
    if (userId) {
      socket.emit("joinLocation", {
        userId,
        lat: location.lat,
        lng: location.lng,
      });

      socket.on("locationUpdate", ({ lat, lng }) => {
        setLocation({ lat, lng });
        setIframeUrl(
          `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${
            lat - 0.01
          },${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`
        );
      });

      return () => {
        socket.off("locationUpdate");
      };
    }
  }, [userId]);

  const handleSubServices = (subservice) => { 
    navigate(`/service_details?subservice=${subservice}`);
  };

  const handleApproveCart = async (booking) => { 
    try {
      const res = await axios.put(
        `https://wavetechservices.in/api/user/approve/${booking._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ); 
      message.success("Cart Approved");
    } catch (err) {
      console.error("Error:", err.response);
      message.error(err.response.data.message);
    }
  };

  const handleDelete = async (booking) => {
    if (!cancelReason) {
      toast.error("Please enter Reason for Cancellation");
      return;
    }
    try {
      const res = await axios.put(
        `https://wavetechservices.in/api/user/bookings/${booking._id}/cancel`,
        {
          cancellationReason: cancelReason,
          userId: user.userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ); 
      message.success("Booking cancelled successfully");
      getBooking();
    } catch (error) {
      toast.error(error.response?.data?.message);
      console.error("Error canceling the Booking:", error);
    }
  };


  const formatDateTime = (dateString) => format(new Date(dateString), "PPpp");
  const formatDateOnly = (dateString) => format(new Date(dateString), "PP");
  const formatTimeOnly = (timeString) =>
    format(new Date(`1970-01-01T${timeString}`), "hh:mm a");

  const isBookingOld = (booking) => {
    const createdAt = new Date(booking.createdAt);
    const currentTime = new Date();
    const timeDifference = (currentTime - createdAt) / (1000 * 60 * 60);
    return timeDifference <= 1;
  };

  const statusTags = {
    completed: (
      <Tag color="green" className="flex items-center gap-1">
        <CheckCircleOutlined /> Completed
      </Tag>
    ),
    pending: (
      <Tag color="orange" className="flex items-center gap-1">
        <ClockCircleOutlined /> Pending
      </Tag>
    ),
    accepted: (
      <Tag color="blue" className="flex items-center gap-1">
        <SyncOutlined spin /> Accepted
      </Tag>
    ),
    paused: (
      <Tag color="orange" className="flex items-center gap-1">
        <ClockCircleOutlined /> Paused
      </Tag>
    ),
    cancelled: (
      <Tag color="red" className="flex items-center gap-1">
        <CloseCircleOutlined /> Canceled
      </Tag>
    ),
  };

  const downloadPDF = async (id) => {
    const element = receiptRef.current;
    const button = element?.querySelector(".joinus-btn");

    if (button) button.style.display = "none";

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`Receipt_${id}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF");
    } finally {
      if (button) button.style.display = "block";
    }
  };

  const handleRatingChange = (bookingId, value) => {
    setRatings(prev => ({ ...prev, [bookingId]: value }));
  };

  const handleCommentChange = (bookingId, value) => {
    setComments(prev => ({ ...prev, [bookingId]: value }));
  };

  const handleSubmitReview = async (bookingId, partnerId) => {
    const rating = ratings[bookingId];
    const comment = comments[bookingId];

    if (!rating || !comment?.trim()) {
      toast.error("Please provide both rating and review");
      return;
    }

    try {
      const response = await axios.post(
        `https://wavetechservices.in/api/user/review/partner`,
        {
          bookingId,
          partnerId,
          rating,
          comment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ); 

      if (response.data.message) {
        message.success("Review submitted successfully!");
        // Clear the review for this booking
        setRatings(prev => ({ ...prev, [bookingId]: 0 }));
        setComments(prev => ({ ...prev, [bookingId]: "" }));
        getBooking(); // Refresh to show the review was submitted
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      message.error("Failed to submit review");
    }
  };

  const truncateText = (text, maxLength = 20) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

if(loading){
return (<div className="flex flex-row gap-2">
  <div className="animate-pulse bg-gray-300 w-14 h-14 rounded-lg"></div>
  <div className="flex flex-col gap-2">
    <div className="animate-pulse bg-gray-300 w-28 h-5 rounded-lg"></div>
    <div className="animate-pulse bg-gray-300 w-36 h-3 rounded-lg"></div>
    <div className="animate-pulse bg-gray-300 w-36 h-2 rounded-lg"></div>
  </div>
</div>)

}

  const renderBookings = (status) => (
    <div className="grid grid-cols-1 gap-4">
      <Toaster />
      {Booking.filter((booking) => booking.status === status).map((booking) => (
        <Card
          key={booking._id}
          className="shadow-md hover:shadow-lg transition-all p-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Service Image */}
            <div className="w-full md:w-40 flex-shrink-0">
              <AntImage
                src={booking?.subService?.icon[0]}
                alt="Service"
                className="w-full h-auto object-cover rounded-md"
                preview={false}
              />
            </div>
            
            {/* Booking Details - Improved alignment */}
            <div className="flex-grow">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <Title level={5} className="!mb-0 !text-lg">
                  {booking?.subService?.name}
                </Title>
                <div>{statusTags[status]}</div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <div className="flex items-start">
                  <span className="text-gray-500 min-w-[120px]">Booking Date:</span>
                  <span>{formatDateOnly(booking.scheduledDate)}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-gray-500 min-w-[120px]">Booking ID:</span>
                  <span className="font-mono">{truncateText(booking._id, 10)}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-gray-500 min-w-[120px]">Amount:</span>
                  <div className="flex items-center gap-2">
                    <span>₹{booking.subService?.price}</span>
                    <Tag color="green" className="m-0">
                      {booking?.paymentMode}
                    </Tag>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-gray-500 min-w-[120px]">Discount:</span>
                  <span>
                    ₹{booking.amount === booking.subService?.price &&
                      booking.subService.discount
                        ? booking.amount -
                          booking.amount * (booking.subService?.discount / 100)
                        : booking.amount}
                  </span>
                </div>
                {booking.partner && (
                  <div className="flex items-start sm:col-span-2">
                    <span className="text-gray-500 min-w-[120px]">Provider:</span>
                    <span>{booking.partner?.profile.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <Button
              onClick={() => handleSubServices(booking?.subService?._id)}
              className="flex items-center justify-center gap-1"
              icon={<CalendarCheck size={16} />}
            >
              Book Again
            </Button>
            
            {booking.partner && (
              <Dialog>
                <DialogTrigger>
                  <Button className="flex items-center justify-center gap-1">
                    Partner Details
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="mb-2">Partner Details</DialogTitle>
                    <Separator className="mb-4" />
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                      <AntImage
                        src={booking.partner?.profilePicture}
                        alt="Partner"
                        width={80}
                        height={80}
                        className="rounded-full"
                        preview={false}
                      />
                      <div className="space-y-2 text-sm">
                        <p><strong>Name:</strong> {booking.partner?.profile.name}</p>
                        <p><strong>Bookings:</strong> {booking.partner?.bookings.length}</p>
                        <p><strong>Experience:</strong> {booking.partner?.experience} years</p>
                        <p><strong>Qualification:</strong> {booking.partner?.qualification}</p>
                        <p><strong>Reviews:</strong> {booking.partner?.reviews?.length}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex flex-wrap gap-2">
                      <Dialog>
                        <DialogTrigger>
                          <Button icon={<ShoppingCart size={16} />}>
                            View Booking Cart
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Booking Cart</DialogTitle>
                          </DialogHeader>
                          <div className="max-h-[500px] overflow-y-auto">
                            {booking.cart?.length === 0 ? (
                              <div className="text-center text-gray-500 py-4">
                                Your cart is empty
                              </div>
                            ) : (
                              booking.cart?.map((product, index) => (
                                <Card key={index} className="mt-4 p-4">
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    <AntImage
                                      width={120}
                                      height={120}
                                      src={product?.product?.image}
                                      alt="Product"
                                      className="rounded-lg object-cover"
                                      preview={false}
                                    />
                                    <div>
                                      <Title level={5} className="!mb-1">
                                        {product?.product?.name}
                                      </Title>
                                      <Text type="secondary" className="block mb-2">
                                        {product?.product?.description}
                                      </Text>
                                      <p className="mb-1">
                                        <strong>Quantity:</strong> {product?.quantity}
                                      </p>
                                      <p className="mb-1">
                                        <strong>Model:</strong> {product?.product?.model}
                                      </p>
                                      <div className="flex gap-2 mb-2">
                                        {product?.product?.discountPercentage > 0 && (
                                          <Tag color="red">
                                            -{product?.product?.discountPercentage}% Off
                                          </Tag>
                                        )}
                                        <Tag color="blue">
                                          GST: {product?.product?.gstPercentage}%
                                        </Tag>
                                      </div>
                                      <p className="font-semibold">
                                        {product?.product?.discountPercentage > 0 ? (
                                          <>
                                            <span className="line-through text-gray-500 mr-2">
                                              ₹{product?.product?.price}
                                            </span>
                                            <span className="text-green-600">
                                              ₹
                                              {product?.product?.price -
                                                product?.product?.price *
                                                (product?.product?.discountPercentage / 100)}
                                            </span>
                                          </>
                                        ) : (
                                          <span>₹{product?.product?.price}</span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </Card>
                              ))
                            )}
                          </div>
                          <Separator className="my-4" />
                          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="font-semibold">
                              SUBTOTAL: ₹
                              {booking.cart
                                ?.reduce((acc, product) => {
                                  const discountedPrice =
                                    product?.product?.price -
                                    product?.product?.price *
                                    (product?.product?.discountPercentage / 100);
                                  return acc + discountedPrice * product?.quantity;
                                }, 0)
                                .toFixed(2)}
                            </div>
                            <Button
                              onClick={() => handleApproveCart(booking)}
                              className="w-full sm:w-auto"
                            >
                              Approve Booking Cart
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      {booking.status !== "completed" && (
                        <Dialog>
                          <DialogTrigger>
                            <Button icon={<IoChatboxEllipsesOutline size={16} />}>
                              Chat with Partner
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-3">
                                <AntImage
                                  src={booking?.partner?.profilePicture}
                                  alt="Partner"
                                  width={48}
                                  height={48}
                                  className="rounded-full"
                                  preview={false}
                                />
                                <span>{booking?.partner?.profile?.name}</span>
                              </DialogTitle>
                            </DialogHeader>
                            <Chat booking={booking} />
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            )}
            
            {booking.status === "paused" && (
              <Dialog>
                <DialogTrigger>
                  <Button>Paused Details</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Rescheduled Booking</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <div>
                      <strong>Paused On:</strong>{" "}
                      {formatDateTime(booking.pauseDetails.pausedAt)}
                    </div>
                    <div>
                      <strong>New Date:</strong>{" "}
                      {formatDateOnly(booking.pauseDetails.nextScheduledDate)}
                    </div>
                    <div>
                      <strong>New Time:</strong>{" "}
                      {formatTimeOnly(booking?.pauseDetails?.nextScheduledTime)}
                    </div>
                    <div>
                      <strong>Reason:</strong>{" "}
                      <em>{booking.pauseDetails.pauseReason}</em>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {booking.status !== "completed" &&
            booking.status !== "cancelled" && 
            booking.status !== "pending" && (
               
                <Dialog>
  
  <DialogTrigger>
                   <Button icon={<LocateFixedIcon size={16} />}>
                     Live Tracking
                   </Button>
                 </DialogTrigger>
  <DialogContent className="h-[90vh] max-h-[1000px] w-[90vw] max-w-[1200px] p-0 border-0 overflow-y-scroll  bg-transparent"> 
    <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"></div>
    <div className="relative z-10 h-full w-full">
      <DialogHeader className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-gray-900 to-transparent p-6">
        <DialogTitle className="text-2xl font-bold text-white">Live Service Tracking</DialogTitle>
        <DialogDescription className="text-gray-300">
          Real-time tracking for {booking.partner.profile.name}
        </DialogDescription>
      </DialogHeader>
      <div className="h-full pt-24">      
        <LiveTracking booking={booking} /> 
      </div>
    </div>
  </DialogContent>
</Dialog>
            )}
              
            {booking.status !== "completed" &&
            booking.status !== "cancelled" && 
            !booking.currentBooking && 
            isBookingOld(booking) && (
              <Dialog>
                <DialogTrigger>
                  <Button icon={<MdCancel size={16} />} danger>
                    Cancel
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Cancel Booking?</DialogTitle>
                    <DialogDescription>
                      <div className="mt-4 space-y-2">
                        <label className="font-medium">Reason for cancellation:</label>
                        <textarea
                          className="w-full border rounded-md p-2 text-sm min-h-[120px]"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          placeholder="Please explain why you're canceling..."
                        />
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button 
                      type="primary" 
                      danger
                      onClick={() => handleDelete(booking)}
                    >
                      Confirm Cancellation
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            
            <Dialog>
              <DialogTrigger>
                <Button icon={<CalendarCheck size={16} />}>
                  Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Invoice</DialogTitle>
                </DialogHeader>
                <Invoice booking={booking} />
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Reviews Section - Fixed to work per booking */}
          {booking.status === "completed" && booking.partner && (
            <div className="mt-6 pt-4 border-t">
              <h3 className="font-medium mb-3">Rate The Service Provider</h3>
              <div className="flex flex-col gap-4">
                <Rate
                  value={ratings[booking._id] || 0}
                  onChange={(value) => handleRatingChange(booking._id, value)}
                />
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[100px]"
                  value={comments[booking._id] || ""}
                  onChange={(e) => handleCommentChange(booking._id, e.target.value)}
                  placeholder="Write your review here..."
                />
                <Button
                  type="primary"
                  onClick={() => handleSubmitReview(booking._id, booking.partner._id)}
                >
                  Submit Review
                </Button>
              </div>
            </div>
          )}
        </Card>
      ))}
      {Booking.filter((booking) => booking.status === status).length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No bookings found for this status.
        </div>
      )}
    </div>
  );

  return (
    <div className="container mt-20 mx-auto px-4 py-8 max-w-7xl">
      <h1 className="text-2xl font-semibold text-center mb-6">
        Booking History
      </h1>
      <Tabs
        defaultActiveKey="pending"
        centered
        className="w-full"
        items={[
          {
            key: "pending",
            label: "Pending",
            children: renderBookings("pending"),
          },
          {
            key: "accepted",
            label: "Accepted",
            children: renderBookings("accepted"),
          },
          {
            key: "paused",
            label: "Paused",
            children: renderBookings("paused"),
          },
          {
            key: "completed",
            label: "Completed",
            children: renderBookings("completed"),
          },
          {
            key: "canceled",
            label: "Canceled",
            children: renderBookings("cancelled"),
          },
        ]}
      />
    </div>
  );
};

export default BookingHistory;